import React, {useCallback, useMemo, useEffect, useState} from 'react';
import dayjs from 'dayjs';
import {useParams, useSearchParams} from 'react-router-dom';
import {
  Check,
  Clock,
  Mail,
  AlertCircle,
  HelpCircle,
  EyeOff,
  Languages,
} from 'lucide-react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';

import {useClientInfoCtx} from '../ClientInfoProvider';
import {ChatInput} from './ChatInput';
import {ackMessage} from '../net/query/AckMessage.gql';
import {useChat} from '../net/query/Chat.gql';
import {useTranslation, Languages as LanguageOptions} from '../Translate';
import type {ChatQuery} from '../net/types/graphql';
import {MsgStatus, DeviceType} from '../net/types/graphql';
import {MediaMessage} from './Mms';
import {useIsAdmin} from '../state/hooks';
import googleTranslateIcon from '../img/goog-trans-greyscale-short.svg';
import {Alert, AlertDescription} from '../components/ui/alert';
import {Button} from '../components/ui/button';
import {Badge} from '../components/ui/badge';
import {Skeleton} from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {useToast} from '../components/ui/toast';

type MessageData = ChatQuery['getMessageHistory'][number];

/**
 * Properties used to render a message.
 */
export type MessageProps = {
  clientId: string;
  data: {
    data: MessageData;
    renderTime: boolean;
    renderName: boolean;
  };
  selected?: boolean;
  setSelected(id: string): void;
  suppressScroll?: boolean;
  clientLang?: string;
};

/**
 * Get hover text for the message status icon.
 */
export const getStatusTitle = (
  msgStatus: MsgStatus | null | undefined,
  code: number | null | undefined,
) => {
  switch (msgStatus) {
    case MsgStatus.Delivered:
      return 'Delivered';
    case MsgStatus.Queued:
    case MsgStatus.Accepted:
      return 'Queued';
    case MsgStatus.Sent:
      return 'Sent';
    case MsgStatus.Failed:
      return `Failed: code ${code}`;
    case MsgStatus.NotSent:
      return 'Not sent';
    case MsgStatus.Undelivered:
      return `Undelivered: code ${code}`;
    case MsgStatus.Void:
      return 'Void';
    default:
      return 'Unknown';
  }
};

/**
 * Get the style for the message status icon.
 */
export const getStatusStyle = (
  msgStatus: MsgStatus | null | undefined,
): React.CSSProperties => {
  switch (msgStatus) {
    case MsgStatus.Failed:
    case MsgStatus.NotSent:
    case MsgStatus.Undelivered:
      return {
        color: 'var(--destructive)',
        fontSize: '1rem',
      };
    case MsgStatus.Delivered:
    case MsgStatus.Received:
      return {
        color: 'var(--constructive)',
        fontSize: '1rem',
      };
    default:
      return {
        color: 'var(--warning)',
        fontSize: '1rem',
      };
  }
};

/**
 * Get the icon to display to indicate the message status.
 */
export const getStatusIcon = (msgStatus: MsgStatus | null | undefined) => {
  switch (msgStatus) {
    case MsgStatus.Delivered:
      return <Check className="h-3 w-3" />;
    case MsgStatus.Queued:
    case MsgStatus.Accepted:
      return <Clock className="h-3 w-3" />;
    case MsgStatus.Sent:
      return <Mail className="h-3 w-3" />;
    case MsgStatus.Failed:
    case MsgStatus.NotSent:
    case MsgStatus.Undelivered:
      return <AlertCircle className="h-3 w-3" />;
    case MsgStatus.Void:
      return <EyeOff className="h-3 w-3" />;
    default:
      return <HelpCircle className="h-3 w-3" />;
  }
};

/**
 * Descriptions for why the UI might be locked (if it's locked).
 */
const LOCK_REASONS = {
  experiment: (
    <>
      <p>
        This client is enrolled in an experimental control group to evaluate the
        effectiveness of our automated messaging. They are <strong>not</strong>{' '}
        receiving any text messages.
      </p>
      <p>
        The messages you see here are placeholders that indicate when we{' '}
        <em>would have</em> sent a message, if the client were not being used as
        a control. The client <strong>cannot</strong> see these placeholders.
      </p>
      <p>
        Messaging functionality has been disabled for this client while they are
        in the experiment. Please contact an admin for help if needed.
      </p>
    </>
  ),
  partialControl: (
    <>
      <p>
        Some of the messages to this client have been logged as part of an
        experimental control treatment.
      </p>
      <p>
        The faded blue messages in the thread are placeholders for messages that
        we could have sent, but did not. The client{' '}
        <strong>cannot see the faded blue messages</strong>! They can only see
        the solid blue messages.
      </p>
      <p>
        You are able to send messages to this client as normal. Please contact
        an admin if you have any questions.
      </p>
    </>
  ),
  optOut: (
    <>
      <p>This client has opted out of receiving text messages.</p>
      <p>
        We cannot send them text messages through Nudge. If you need to get in
        contact with them, you can try calling them or texting them through your
        own phone number.
      </p>
    </>
  ),
  other: (
    <>
      <p>Messaging functionality has been disabled for this client.</p>
      <p>Please contact an admin for help if needed.</p>
    </>
  ),
};

/**
 * Check if there was a significant delay between two messages.
 *
 * This delay is completely heuristic and is used to render the timestamp
 * in the chat window.
 */
export const checkMessageGap = (a: MessageData, b: MessageData) => {
  const diff = dayjs(a.sent).diff(b.sent, 'hour');

  // Check for a one hour gap in messages.
  return Math.abs(diff) >= 1;
};

/**
 * Get a unique name of a file for a media attachment.
 */
export const getFileName = (data: MessageData, mimeType: string, i: number) => {
  const name = `${data.source?.account?.fullName || 'Unknown_Client'}-${
    data.seq
  }-${i}`.replace(/[^\w\d-]+/g, '_');
  const ext = mimeType.split('/')[1];
  return `${name}.${ext}`;
};

export type MessageContentMeta = {
  translation?: string | null;
};

/**
 * Render text or media message content.
 */
export const renderMessageContent = (
  data: MessageData,
  meta?: MessageContentMeta,
) => {
  if (data.mediaUrls.length) {
    return data.mediaUrls.map((media, i) => (
      <MediaMessage
        key={media.uri}
        url={media.uri}
        name={getFileName(data, media.contentType, i)}
        mimeType={media.contentType}
      />
    ));
  }

  if (!data.content) {
    return null;
  }

  return (
    <>
      <div className="-original text-sm whitespace-pre-wrap">
        {data.content}
      </div>
      {meta?.translation ? (
        <>
          <div className="-translation py-3 text-secondary">
            {meta?.translation}
          </div>
          <div className="-translation-attr">
            <img src={googleTranslateIcon} alt="Google Translate" width={75} />
          </div>
        </>
      ) : null}
    </>
  );
};

/**
 * One single message bubble.
 */
export const Message = ({
  data,
  selected,
  setSelected,
  suppressScroll,
}: MessageProps) => {
  const {showToast} = useToast();
  const [doTranslate, requestTranslation] = useState(false);
  const [sourceLang, setSourceLang] = useState<string | undefined>(undefined);
  const translation = useTranslation({
    skip: !doTranslate,
    text: data.data.content,
    into: 'en',
    source: sourceLang,
  });

  // Display error from translation.
  useEffect(() => {
    if (translation.error) {
      showToast(`Error translating message: ${translation.error}`, 'error');
    }
  }, [translation.error, showToast]);

  // Update the source language from Google's auto-detection.
  useEffect(() => {
    if (translation.fromLang) {
      setSourceLang(translation.fromLang);
    }
  }, [translation.fromLang]);

  // When a selected node is rendered, scroll it into view in the center of
  // the screen, using smooth scrolling if possible.
  const ref = useCallback(
    (node: HTMLDivElement) => {
      if (!node || !selected || suppressScroll) {
        return;
      }

      node.scrollIntoView({behavior: 'smooth', block: 'center'});
    },
    [selected, suppressScroll],
  );

  const sourceType = data.data.source?.account?.__typename;
  const incoming =
    sourceType === 'ClientAccount' ||
    sourceType === 'UnknownAccount' ||
    sourceType === 'AssociateAccount';

  return (
    <>
      {data.renderTime && (
        <div className="text-xs text-muted-foreground text-center p-2">
          {dayjs(data.data.sent).format('lll')}
        </div>
      )}
      {data.renderName && data.data.source?.account?.id ? (
        <div
          className={`text-sm font-medium px-4 py-1 ${
            incoming ? 'text-left' : 'text-right'
          }`}
        >
          {data.data.source.account.fullName || 'Unknown'}
        </div>
      ) : null}
      <div
        className={`flex ${
          incoming ? 'justify-start' : 'justify-end'
        } px-4 py-1 items-center gap-2 cursor-default`}
      >
        <div
          className={`max-w-[70%] rounded-lg p-2 relative ${
            incoming
              ? 'bg-muted text-foreground'
              : 'bg-primary text-primary-foreground'
          } ${selected ? 'ring-3 ring-yellow-500' : ''} ${
            translation.translating ? 'opacity-75' : ''
          } ${data.data.status === MsgStatus.Void ? 'opacity-50' : ''}`}
          ref={selected ? ref : undefined}
          onClick={(e) => {
            // Select the current message and dispose the event.
            setSelected(data.data.id);
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {renderMessageContent(data.data, {
            translation: translation.translation,
          })}

          {translation.translation ? (
            <div className="mt-2">
              <Select value={sourceLang} onValueChange={setSourceLang}>
                <SelectTrigger className="h-6 text-xs">
                  <SelectValue placeholder="Select source language" />
                </SelectTrigger>
                <SelectContent>
                  {LanguageOptions.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {selected ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {data.data.meta.tags.length
                ? data.data.meta.tags.map((x) => (
                    <Badge key={x} variant="outline" className="text-xs">
                      {x}
                    </Badge>
                  ))
                : null}
            </div>
          ) : null}
        </div>

        {data.data.content &&
        incoming &&
        translation.canTranslate &&
        selected ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            disabled={!translation.canTranslate}
            onClick={() => requestTranslation(true)}
          >
            <Languages className="h-3 w-3" />
          </Button>
        ) : translation.translation ? (
          <div>
            <Languages className="h-3 w-3 text-muted-foreground" />
          </div>
        ) : null}

        {!incoming && (
          <div
            className="text-muted-foreground"
            style={getStatusStyle(data.data.status)}
            title={getStatusTitle(data.data.status, data.data.errorCode)}
          >
            {getStatusIcon(data.data.status)}
          </div>
        )}
      </div>
    </>
  );
};

/**
 * View for the chat message history.
 */
export const Chat = () => {
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const {clientInfo} = useClientInfoCtx();
  const isAdmin = useIsAdmin();
  const {showToast} = useToast();
  const [suppressScroll, setSuppressScroll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const {accountId} = useParams();
  const selectedMsgId = searchParams.get('m');
  const {data, loading, error, fetchMore} = useChat({
    pollInterval: 60000,
    variables: {
      id: accountId!,
      limit: 30,
    },
  });

  // If there is a selected message, make sure that it is loaded. The API
  // wasn't designed for arbitrary querying in the middle of the convo like
  // this, so we just have to load pages until we find it. Obviously this isn't
  // performant. We don't currently expect this to be used super often or the
  // chat histories to be super long, so it's good enough for now.
  useEffect(() => {
    if (!selectedMsgId) {
      return;
    }

    if (!data?.hasMoreMessages || !data?.getMessageHistory) {
      return;
    }

    if (!data.getMessageHistory.find((item) => item.id === selectedMsgId)) {
      fetchMore({
        variables: {offset: data.getMessageHistory[0]?.id, limit: 15},
      });
    }
  }, [fetchMore, data, selectedMsgId]);

  // NOTE: sorting is expected to happen in the cache, so it's never done
  // during render.
  const messages = (data?.getMessageHistory?.slice() || []).map((m, i, l) => ({
    data: m,
    renderTime: i === 0 || checkMessageGap(l[i - 1], m),
    renderName: i === 0 || l[i - 1].source.account?.id !== m.source.account?.id,
  }));

  // Check if every one of our automated messages is delivered silently. This
  // means that the client is completely in a control group and we have not
  // sent them any actual messages.
  const automatedMessages = messages.filter((m) =>
    /^Automated/.test(m.data.source.account?.fullName || ''),
  );
  const isSilent =
    automatedMessages.length > 0 &&
    automatedMessages.every((m) => m.data.status === MsgStatus.Void);
  // Check if we are sending some (but not all) automated messages silently.
  // This implies that the client is in some control group, but not every
  // control group.
  const isPartiallySilent =
    automatedMessages.length > 0 &&
    !isSilent &&
    automatedMessages.some((m) => m.data.status === MsgStatus.Void);
  // Check if client has opted out of text messages so that we can disable the
  // UI and explain the situation to the staff user.
  const mobileDevices =
    clientInfo?.devices?.filter((d) => d.type === DeviceType.Mobile) || [];
  const isOptOut =
    mobileDevices.length > 0 && mobileDevices.every((d) => d.optedOut);
  // TODO: show warning if *some* device is opted out, but not every.
  const lockReason = isSilent
    ? 'experiment'
    : isPartiallySilent
      ? 'partialControl'
      : isOptOut
        ? 'optOut'
        : null;
  // Do not lock the chat UI if the user is an admin, or if the client is
  // in the partial control group.
  const lockChatUi = (isSilent && !isAdmin) || (isOptOut && !isAdmin);
  const showLockWarning = !!lockReason;

  if (!loading) {
    if (error) {
      useMemo(
        () =>
          setTimeout(
            () =>
              showToast(
                `Error loading chat thread: ${error.message || error}`,
                'error',
              ),
            0,
          ),
        [showToast],
      );
      return (
        <div className="p-4 text-center text-muted-foreground">
          An error occurred :(
        </div>
      );
    }

    if (!data?.getMessageHistory) {
      useMemo(
        () =>
          setTimeout(
            () => showToast(`Chat data appears to be malformed`, 'error'),
            0,
          ),
        [showToast],
      );
      return (
        <div className="p-4 text-center text-muted-foreground">
          An error occurred :(
        </div>
      );
    }
  }

  useEffect(() => {
    if (loading || error) {
      return;
    }

    for (const msg of messages) {
      ackMessage(apolloClient, msg.data);
    }
  }, [loading, error, messages]);

  return (
    <>
      {showLockWarning ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{LOCK_REASONS[lockReason]}</AlertDescription>
        </Alert>
      ) : null}
      <div
        id="nudge-chat-window"
        className="flex-1 overflow-y-auto overflow-x-visible flex flex-col-reverse"
        onClick={() => {
          // Reset the selected message if there is one.
          if (searchParams.get('m')) {
            setSearchParams({m: ''});
          }
        }}
      >
        <InfiniteScroll
          scrollableTarget="nudge-chat-window"
          style={{overflow: 'visible'}}
          dataLength={messages.length}
          next={() => {
            // Suppress scrolling behavior when scrolling away from the selected
            // message so that scroll doesn't jump.
            if (selectedMsgId) {
              setSuppressScroll(true);
            }
            fetchMore({
              variables: {offset: messages[0]?.data?.id, limit: 15},
            });
          }}
          hasMore={data ? data.hasMoreMessages : true}
          loader={
            messages.length > 0 &&
            loading && (
              <div className="p-4">
                <Skeleton className="h-16 w-full" />
              </div>
            )
          }
          inverse={true}
        >
          <div className="space-y-2">
            {messages.map((item) => (
              <Message
                key={item.data.id}
                clientId={accountId!}
                data={item}
                selected={selectedMsgId === item.data.id}
                suppressScroll={suppressScroll}
                setSelected={(id) => {
                  // Make sure scrolling is not suppressed, if it was before.
                  setSuppressScroll(false);
                  setSearchParams({m: id});
                }}
              />
            ))}
          </div>
        </InfiniteScroll>
      </div>
      <div id="nudge-compose-box grow-1 z-10">
        <ChatInput disable={lockChatUi} />
      </div>
    </>
  );
};
